import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator, RefreshControl, Modal, Animated, Image, useWindowDimensions } from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { eventsAPI, apiClient } from '../services/api';
import { Logger } from '../utils/logger';
import { WebPageShell } from '../components/common/WebPageShell';
import { formatLocalTime, parseBackendDate } from '../utils/dateUtils';
import { useTheme, ThemeColors } from '../context/ThemeContext';

interface UIEvent {
  id: string;
  dateNum: string;
  dateMonth: string;
  title: string;
  details: string;
  description: string;
  fileUrl: string;
  fullDateRange: string;
  location: string;
}

export function EventsScreen({ isNested }: { isNested?: boolean }) {
  const [events, setEvents] = useState<UIEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<UIEvent | null>(null);
  const [selectedEventImage, setSelectedEventImage] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const openModal = async (evt: UIEvent) => {
    setSelectedEvent(evt);
    setSelectedEventImage(null);

    const finalUrl = evt.fileUrl?.trim();
    if (!finalUrl) {
      return;
    }

    if (finalUrl.startsWith('data:') || finalUrl.startsWith('blob:')) {
      setSelectedEventImage(finalUrl);
      return;
    }

    const isBase64 = finalUrl.startsWith("/9j/") || finalUrl.startsWith("iVBOR") || finalUrl.startsWith("R0lGOD") || finalUrl.startsWith("UklGR");
    if (isBase64) {
      let cType = "image/jpeg";
      if (finalUrl.startsWith("iVBOR")) cType = "image/png";
      else if (finalUrl.startsWith("R0lGOD")) cType = "image/gif";
      else if (finalUrl.startsWith("UklGR")) cType = "image/webp";
      setSelectedEventImage(`data:${cType};base64,${finalUrl}`);
      return;
    }

    try {
      setImageLoading(true);
      const { data } = await apiClient.get(finalUrl);
      if (data && data.imageBase64) {
        const cType = data.contentType || 'image/jpeg';
        setSelectedEventImage(`data:${cType};base64,${data.imageBase64}`);
      } else {
        setSelectedEventImage(null);
      }
    } catch (err) {
      Logger.warn('EventsScreen', 'Failed to load event image', err);
    } finally {
      setImageLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await eventsAPI.getEvents();
      const rawEvents = data?.Events ?? data?.events ?? [];

      const mapped: UIEvent[] = rawEvents.map((r: any) => {
        const rawDate = r.eventDate || r.EventDate || r.startDate || r.StartDate || new Date().toISOString();
        const d = new Date(rawDate);

        const dateNum = d.toLocaleDateString('en-IN', { day: '2-digit' });
        const dateMonth = d.toLocaleDateString('en-IN', { month: 'short' }).toUpperCase();

        const title = r.title || r.Title || 'School Event';

        let time = r.eventTime || r.EventTime;
        const tStart = r.startDateTime || r.StartDateTime || rawDate;
        const tEnd = r.endDateTime || r.EndDateTime;

        if (!time) {
          // formatLocalTime, not new Date(...).toLocaleTimeString: event times come
          // back with no timezone marker, so a plain parse showed every event at a
          // clock time shifted by the device's UTC offset (see dateUtils).
          const tStartStr = formatLocalTime(tStart);
          if (tEnd) {
            time = `${tStartStr} - ${formatLocalTime(tEnd)}`;
          } else {
            time = tStartStr;
          }
        }

        const loc = r.location || r.Location || 'Campus';
        const details = `${time} • ${loc}`;
        const description = r.description || r.Description || '';
        const id = r.id || r.Id || r.eventId || r.EventId || Math.random().toString();
        const hasImage = !!(r.fileUrl || r.FileUrl || r.downloadUrl || r.DownloadUrl || r.imageUrl || r.ImageUrl);
        const fileUrl = hasImage ? `/Event/download/image/${id}` : '';

        const fmtDate = (iso: string) => {
          try {
            const d = parseBackendDate(iso);
            if (!d) return iso;
            const dateStr = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            const timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
            return `${dateStr} • ${timeStr}`;
          } catch {
            return iso;
          }
        };

        let fullDateRange = fmtDate(tStart);
        if (tEnd && tEnd !== tStart) {
          fullDateRange = `${fmtDate(tStart)}  -  ${fmtDate(tEnd)}`;
        }

        return {
          id,
          dateNum,
          dateMonth,
          title,
          details,
          description,
          fileUrl,
          fullDateRange,
          location: loc
        };
      });

      // Sort by soonest first
      setEvents(mapped);
    } catch (err) {
      Logger.error('EventsScreen', 'Failed to load events', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const onRefresh = () => {
    setRefreshing(true);
    loadEvents();
  };

  if (loading) {
    const loader = <LoadingSpinner message="Loading events..." />;
    // On its own the page keeps its header while it loads; inside Communication it is just the loader.
    return isNested ? loader : <WebPageShell title="Events" maxWidth={1000}>{loader}</WebPageShell>;
  }

  const content = (
    <ScrollView
      style={[styles.container, isNested && { backgroundColor: 'transparent' }]}
      contentContainerStyle={[styles.content, isMobile && styles.contentMobile]}
      scrollEnabled={!isNested}
      refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
    >
      {events.map((evt) => (
        <TouchableOpacity key={evt.id} style={[styles.card, isMobile && styles.cardMobile]} onPress={() => openModal(evt)}>
          <View style={styles.dateBox}>
            <Text style={styles.dateNum}>{evt.dateNum}</Text>
            <Text style={styles.dateMonth}>{evt.dateMonth}</Text>
          </View>
          <View style={styles.infoBox}>
            <Text style={styles.title}>{evt.title}</Text>
            <Text style={styles.details}>{evt.details}</Text>
          </View>
          <View style={styles.addButton}>
            <Text style={styles.addText}>+</Text>
          </View>
        </TouchableOpacity>
      ))}
      {events.length === 0 && (
        <View style={styles.empty}>
          <Text style={styles.emptyText}>No upcoming events</Text>
        </View>
      )}
      <View style={{ height: 24 }} />

      <Modal visible={!!selectedEvent} transparent animationType="fade" onRequestClose={closeModal}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={closeModal}>
          <View style={[styles.modalContent, isMobile && styles.modalContentMobile]}>
            <TouchableOpacity activeOpacity={1} style={{ flexShrink: 1, width: '100%' }}>
              <View style={[styles.modalHeader, isMobile && styles.modalHeaderMobile]}>
                <Text style={styles.modalTitle} numberOfLines={1}>{selectedEvent?.title}</Text>
                <TouchableOpacity onPress={closeModal} style={styles.closeButton}>
                  <FeatherIcon name="x" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>
              <ScrollView
                contentContainerStyle={[styles.modalBody, isMobile && styles.modalBodyMobile]}
                showsVerticalScrollIndicator={true}
                persistentScrollbar={true}
                fadingEdgeLength={50}
              >
                {imageLoading ? (
                  <View style={[styles.eventImage, { justifyContent: 'center', alignItems: 'center' }]}>
                    <ActivityIndicator color={colors.primary} />
                  </View>
                ) : selectedEventImage ? (
                  <Image
                    source={{ uri: selectedEventImage }}
                    style={styles.eventImage}
                    resizeMode="cover"
                  />
                ) : (
                  <View style={[styles.eventImage, { justifyContent: 'center', alignItems: 'center' }]}>
                    <Text style={{ color: colors.textMuted }}>No Image Available</Text>
                  </View>
                )}
                <View style={styles.modalMetaRowList}>
                  <Text style={[styles.modalMetaText, styles.modalMetaDate]}>{selectedEvent?.fullDateRange}</Text>
                  <View style={styles.modalMetaLocationRow}>
                    <FeatherIcon name="map-pin" size={14} color={colors.textSecondary} />
                    <Text style={styles.modalMetaText}>{selectedEvent?.location}</Text>
                  </View>
                </View>
                {!!selectedEvent?.description && (
                  <Text style={styles.modalDescription}>{selectedEvent?.description}</Text>
                )}
              </ScrollView>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </ScrollView>
  );

  if (isNested) {
    return content;
  }

  return <WebPageShell title="Events" maxWidth={1000}>{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: colors.background,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    paddingHorizontal: 20,
    paddingTop: 8,
  },
  contentMobile: {
    paddingHorizontal: 12,
  },
  card: {
    flexDirection: 'row',
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.divider,
  },
  cardMobile: {
    padding: 12,
  },
  dateBox: {
    backgroundColor: colors.primaryLight,
    borderRadius: 12,
    width: 50,
    height: 56,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  dateNum: {
    fontSize: 18,
    fontWeight: '800',
    color: isDark ? colors.primary : '#217BD6',
  },
  dateMonth: {
    fontSize: 11,
    fontWeight: '700',
    color: isDark ? colors.primary : '#217BD6',
    marginTop: -2,
  },
  infoBox: {
    flex: 1,
    marginRight: 12,
  },
  title: {
    fontSize: 15,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  details: {
    fontSize: 13,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1.5,
    borderColor: colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
  },
  addText: {
    fontSize: 20,
    fontWeight: '400',
    color: isDark ? colors.primary : '#217BD6',
    marginTop: -2,
  },
  empty: {
    padding: 20,
    alignItems: 'center',
  },
  emptyText: {
    color: colors.textMuted,
    fontSize: 14,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalContent: {
    backgroundColor: colors.card,
    borderRadius: 16,
    maxHeight: '90%',
    overflow: 'hidden',
    width: '100%',
    maxWidth: 500,
  },
  modalContentMobile: {
    maxHeight: '95%',
    borderRadius: 12,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  modalHeaderMobile: {
    padding: 14,
  },
  closeButton: {
    padding: 4,
  },
  modalBody: {
    padding: 24,
    paddingTop: 16,
    paddingBottom: 40,
  },
  modalBodyMobile: {
    padding: 16,
    paddingTop: 12,
    paddingBottom: 28,
  },
  eventImage: {
    width: '100%',
    height: 200,
    borderRadius: 16,
    marginBottom: 16,
    backgroundColor: colors.divider,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.textPrimary,
    flex: 1,
    marginRight: 16,
  },
  modalMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalMetaRowList: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    marginBottom: 16,
  },
  modalMetaLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  modalMetaDate: {
    color: isDark ? colors.primary : '#217BD6',
    fontWeight: '600',
  },
  modalMetaText: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  modalDescription: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 24,
  },
});
